import { arrayLiteralToCamelCase } from 'pos-shared/utils/textHelpers.ts';
import { z } from 'zod';

import { createPaginatedResponseSchema } from './pagingSchema.ts';
import { TirePositionEnum } from './vehicleSchema.ts';

export const WarrantyTypeEnum = z.enum(['ProRata', 'FullRefund']);

export const WarrantyTypeCamelEnum = z.enum(
  arrayLiteralToCamelCase(WarrantyTypeEnum.options)
);

export const WarrantyReferenceSchema = z.object({
  warrantyRef: z.uuid(),
  startDate: z.coerce.date<Date>().nullish(),
  endDate: z.coerce.date<Date>().nullish(),
  mileage: z.number().nullish(),
  treadDepth: z.number().nullish(),
});

export const WarrantyCategoryEnum = z.enum([
  'Recall',
  'BuyAndTry',
  'FactoryMileage',
  'RoadHazard',
  'Parts',
  'Battery',
]);

export const TireWarrantyCategoryEnum = WarrantyCategoryEnum.extract([
  'Recall',
  'BuyAndTry',
  'FactoryMileage',
  'RoadHazard',
]);

export const WarrantyValidationQuestionEnum = z.enum([
  'originalPurchaser',
  'originalEquipment',
  'commercialPurposes',
  'installedOnOriginalVehicle',
]);

export const WarrantySchema = z.object({
  id: z.uuid(),
  name: z.string(),
  description: z.string(),
  type: WarrantyTypeEnum,
  category: WarrantyCategoryEnum,
  salesPrice: z.number().nullish(),
  salesPriceTaxAmount: z.number().nullish(),
});

export const PartWarrantyTireFormDotSchema = z.object({
  code: z.string(),
  error: z.string().nullish(),
  imported: z.string().optional(),
});

export const PartWarrantyTireDataSchema = z.object({
  dot: z.string(),
  lineNumber: z.number(),
  originalRTD: z.number(),
  remainingRTD: z.number(),
  wheelPosition: TirePositionEnum.exclude(['None']),
  warrantyReason: z.string(),
  onCarMileage: z.number(),
  offCarMileage: z.number(),
  worn: z.number(),
});

export const PartWarrantyTireFormSchema = PartWarrantyTireDataSchema.omit({
  dot: true,
}).extend({
  dot: PartWarrantyTireFormDotSchema,
});

export const WarrantyPartSchema = z.object({
  orderId: z.uuid(),
  partId: z.uuid(),
  jobId: z.uuid(),
  jobItemId: z.uuid(),
  articleNumber: z.string(),
  partNumber: z.string(),
  description: z.string(),
  listPrice: z.number(),
  quantity: z.number().int(),
  tire: PartWarrantyTireFormSchema.partial().array().optional(),
  sku: z.string(),
});

export const WarrantyClaimFormSchema = z.object({
  orderId: z.string(),
  warrantyProductId: z.string(),
  guaranteedOrDefectiveItem: WarrantyPartSchema,
  newItem: WarrantyPartSchema.omit({ tire: true }),
  warrantyCategory: WarrantyCategoryEnum,
});

const WarrantyTireCalculationDotDepthSchema = z.object({
  dotcode: z.string(),
  treadDepth: z.number(),
});

export const WarrantyTireCalculationSchema = z.object({
  oldOrderId: z.uuid(),
  newOrderId: z.uuid(),
  warrantyId: z.uuid(),
  jobId: z.uuid(),
  jobItemId: z.uuid(),
  dotCodesAndRemainingTreadDepths:
    WarrantyTireCalculationDotDepthSchema.array(),
  currentSellPrice: z.number(),
  partId: z.uuid(),
  originalTreadDepth: z.number(),
});

export const WarrantyTireCalculationResponseSchema = z.object({
  treadDepthWithinMargin: z.boolean(),
  treadDepthCurrent: z.string(),
  totalMiles: z.number(),
  percentageWarrantedMiles: z.number(),
  replacementPrice: z.number(),
  summary: z.string(),
  dotCode: z.string(),
});

export const CreateWarrantySchema = WarrantySchema.omit({
  id: true,
});

export const WarrantiesSearchResponseSchema =
  createPaginatedResponseSchema(WarrantySchema);

const ClaimWarrantyRequestOrderSchema = z.object({
  id: z.uuid(),
  itemId: z.uuid(),
  partId: z.uuid(),
});

const ClaimWarrantyPartDataSchema = z.object({
  calculatedPrice: z.number().nullable(),
  warrantyCalculationString: z.string().nullable(),
  tireData: PartWarrantyTireDataSchema.optional(),
});

export const WarrantyClaimRequestSchema = z.object({
  warrantyId: z.uuid(),
  oldOrder: ClaimWarrantyRequestOrderSchema,
  newOrder: ClaimWarrantyRequestOrderSchema,
  warrantyPartData: ClaimWarrantyPartDataSchema.array().optional(),
});

export const ValidateDotCodeResponseSchema = z.object({
  article: z.string().optional(),
  dotCodeCorrected: z.string().optional(),
  isValid: z.boolean(),
  type: z.string(),
  error: z.string().optional(),
});

export type WarrantyPartType = 'guaranteedOrDefectiveItem' | 'newItem';
const WarrantyTireLabelPartDataSchema = z.object({
  id: z.uuid(),
  lineNumber: z.number(),
  dot: z.string(),
  originalRTD: z.number(),
  remainingRTD: z.number(),
  wheelPosition: z.string(),
  warrantyReason: z.string(),
  onCarMileage: z.number(),
  offCarMileage: z.number(),
  worn: z.number(),
  barcode: z.string(),
});

export const WarrantyTireLabelSchema = z.object({
  id: z.uuid(),
  ticketNumber: z.string(),
  storeNumber: z.string(),
  customerName: z.string(),
  transactionDate: z.coerce.date<Date>(),
  vehicleYMM: z.string(),
  tireDescription: z.string(),
  partWarrantyTireData: WarrantyTireLabelPartDataSchema.array(),
});

export const GetWarrantyTireLabelsResponseSchema = z.object({
  tireLabels: WarrantyTireLabelSchema.array(),
});

export const WarrantyTireLabelPrintSchema = WarrantyTireLabelSchema.omit({
  partWarrantyTireData: true,
})
  .extend(WarrantyTireLabelPartDataSchema.shape)
  .extend({
    lineTotal: z.number(),
  });

export const SellWarrantiesFormSchema = z.object({
  partId: z.uuid(),
  itemId: z.uuid(),
  warranties: WarrantySchema.array().min(1),
});

export const SellWarrantiesRequestSchema = z.object({
  warranties: z
    .object({
      orderId: z.uuid(),
      jobId: z.uuid(),
      itemId: z.uuid(),
      partId: z.uuid(),
      warrantyId: z.uuid(),
    })
    .array(),
});

export type WarrantyCategoryModel = z.infer<typeof WarrantyCategoryEnum>;
export type WarrantyValidationQuestionTypeEnum = z.infer<
  typeof WarrantyValidationQuestionEnum
>;
export type WarrantyModel = z.infer<typeof WarrantySchema>;
export type WarrantiesSearchResponseModel = z.infer<
  typeof WarrantiesSearchResponseSchema
>;
export type WarrantyPartModel = z.infer<typeof WarrantyPartSchema>;
export type CreateWarrantyModel = z.infer<typeof CreateWarrantySchema>;
export type WarrantyReferenceModel = z.infer<typeof WarrantyReferenceSchema>;
export type WarrantyClaimFormModel = z.infer<typeof WarrantyClaimFormSchema>;
export type WarrantyTireCalculationModel = z.infer<
  typeof WarrantyTireCalculationSchema
>;
export type WarrantyTireCalculationResponseModel = z.infer<
  typeof WarrantyTireCalculationResponseSchema
>;
export type PartWarrantyTireFormModel = z.infer<
  typeof PartWarrantyTireFormSchema
>;

export type WarrantyClaimRequestModel = z.infer<
  typeof WarrantyClaimRequestSchema
>;

export type PartWarrantyTireDataModel = z.infer<
  typeof PartWarrantyTireDataSchema
>;
export type ValidateDotCodeResponseModel = z.infer<
  typeof ValidateDotCodeResponseSchema
>;

export type GetWarrantyTireLabelsResponseModel = z.infer<
  typeof GetWarrantyTireLabelsResponseSchema
>;
export type WarrantyTireLabelModel = z.infer<typeof WarrantyTireLabelSchema>;
export type WarrantyTireLabelPrintModel = z.infer<
  typeof WarrantyTireLabelPrintSchema
>;

export type SellWarrantiesRequestModel = z.infer<
  typeof SellWarrantiesRequestSchema
>;
export type SellWarrantiesFormModel = z.infer<typeof SellWarrantiesFormSchema>;
